import React, { ReactElement } from 'react';
import { useFormikContext } from 'formik';
import InjectionButton from '../InjectionButton/InjectionButton';
import { BaseEntite, BaseEntiteSommaireStatique } from '../../../../../../common/src/entities';
import DeepEqual from 'deep-equal';

function compareInjectionValue<TEntite extends BaseEntite>(
    injectionValue: any | TEntite,
    fieldValue: any | TEntite,
): boolean {
    let testResult = false;
    if (injectionValue !== Object(injectionValue)) {
        if (typeof injectionValue === 'string' && typeof fieldValue === 'string') {
            testResult = injectionValue.trim() === fieldValue.trim();
        } else {
            testResult = injectionValue === fieldValue;
        }
    } else if (
        (injectionValue as TEntite).constructor === (fieldValue as TEntite).constructor &&
        (injectionValue as TEntite).id &&
        (fieldValue as TEntite).id
    ) {
        testResult = (injectionValue as TEntite).id === (fieldValue as TEntite).id;
    } else {
        testResult = DeepEqual(injectionValue, fieldValue);
    }

    return testResult;
}

export interface PropsDeChamp<TEntite extends BaseEntite> {
    children: ReactElement | ReactElement[];
    injectionValue?: any | TEntite;
    injectionType?: BaseEntiteSommaireStatique<TEntite>;
    injectionTitle?: string;
    injectionTrace?: string;
}

export default function ChampCascadeContent<TFormulaire, TEntite extends BaseEntite>(
    props: PropsDeChamp<TEntite>,
): ReactElement {
    const { children, injectionValue, injectionType, injectionTitle, injectionTrace } = props;

    const { values, setFieldValue } = useFormikContext<TFormulaire>();

    let childrenIdToInject: string;
    if (children && Array.isArray(children) && children.length) {
        childrenIdToInject = (children[0] as ReactElement).props.id;
    } else {
        childrenIdToInject = (children as ReactElement).props.id;
    }

    let fieldValue: any | TEntite = childrenIdToInject ? values[childrenIdToInject] : undefined;
    if (!Array.isArray(injectionValue) && Array.isArray(fieldValue) && fieldValue.length === 1) {
        // handling for ChampMultiple with max value of 1 & that will be sent as an Entite rather than an array of Entite
        fieldValue = fieldValue[0];
    }

    const injectionButton: (size?: 'sm' | 'lg') => ReactElement = (size?: 'sm' | 'lg') => {
        return (
            <InjectionButton
                onClick={() => setFieldValue(childrenIdToInject, injectionValue)}
                size={size}
                disabled={
                    !injectionValue ||
                    (!!childrenIdToInject && compareInjectionValue<TEntite>(fieldValue, injectionValue))
                }
                injectionTitle={injectionTitle}
                injectionTrace={injectionTrace}
                injectionPreview={injectionValue}
                injectionModel={injectionType}
            />
        );
    };

    const childdrenWithInjectionButton: ReactElement[] = React.Children.map(children, (child) => {
        const childElement: ReactElement = child as ReactElement;
        if (React.isValidElement(childElement as ReactElement)) {
            return React.cloneElement(childElement, {
                appendedChildren: injectionButton(childElement.props?.size),
            });
        }
        return childElement;
    });

    return <>{childdrenWithInjectionButton}</>;
}
